/**
 * Preparation Job Service — "ใบงานเตรียมเครื่อง" (PO 2026-09-08)
 *
 * ช่องว่างที่ปิด: เซลขายได้ → ส่งเรื่องให้ Support เตรียมเครื่อง/ซิม → ส่งมอบ
 * วันนี้ขั้นตอนนี้เกิดนอกระบบทั้งหมด (โทร/LINE) เพราะฟอร์มการเช่าบังคับเลือกเครื่องราย S/N
 * ตั้งแต่วันขาย + ถามข้อมูลหลังบ้าน (รหัสโครงการ/มัดจำ/วันเริ่มเก็บเงิน) ที่เซลตอบไม่ได้
 *
 * **กติกาออกแบบที่ห้ามละเมิด** (เหตุผล: prod เซล 2 คนไม่เคย login เลยใน 5.5 เดือน ·
 * ใบเสนอราคา/สัญญา/ศูนย์ขออุปกรณ์เสริม = 0 แถวทั้งหมด):
 * 1. จอเปิดใบมี **7 ช่อง** เท่านั้น — บังคับ 4 (ลูกค้า, สถานที่, สินค้า+จำนวน, วันที่อยากได้ของ)
 * 2. ลูกค้า/สถานที่ **พิมพ์ชื่อใหม่ได้** ไม่บังคับให้มีใน master ก่อน (ลูกค้าใหม่ยังไม่มีรหัส)
 * 3. **ห้ามมีช่อง** เครื่อง/S/N, ซิม, มัดจำ, วันเริ่มเก็บเงิน, AnyDesk ในจอเปิดใบ
 *    — ของพวกนี้เป็นงาน Support ทำทีหลังในหน้ารายละเอียด (ต้องมี `.edit`)
 *
 * ชนิดข้อมูลทุกตัวในไฟล์นี้ mirror `backend/app/schemas/preparation_job.py` —
 * **แก้ที่นี่ที่เดียวเมื่อ backend เปลี่ยน** อย่ากระจาย field name ไปทั่วหน้าจอ
 */
import ApiWithLogging from '../utils/apiWithLogging';
import { PickerBlockedItem } from '../types/rentalHold';

// ========== สิทธิ์ ==========

/**
 * map action → permission key ที่ backend บังคับจริง (`endpoints/preparation_jobs.py`)
 * — แหล่งเดียว จอทุกจอ import จากที่นี่ ไม่ hardcode key ซ้ำ
 *
 * **ตารางเทียบกับ gate จริงฝั่ง backend** (ตรวจครบทุก endpoint 2026-09-08 · ถ้าแก้ backend ต้องแก้ที่นี่ด้วย):
 * | endpoint | backend gate | ใช้ที่ FE |
 * |---|---|---|
 * | GET /options, /stats, /available-devices, /available-sims, /reserved-items, /, /{id} | `view` | `PREP_PERMS.view` |
 * | GET /products | any(`create`, `view`) | เรียกในฟอร์มเปิดใบ (มี create อยู่แล้ว) |
 * | GET /export/excel | export perm **และ** `view` | `canExport('preparation_jobs')` |
 * | POST / | `create` | `PREP_PERMS.create` |
 * | PUT /{id} (แก้หัวใบ) | any(`edit`, `create`) | `PREP_EDIT_HEAD_PERMS` |
 * | PUT /{id}/items (จับคู่อุปกรณ์), DELETE /{id} | `edit` | `PREP_PERMS.edit` |
 * | POST /{id}/accept, /assign | `assign` | `PREP_PERMS.accept` / `.assign` |
 * | POST /{id}/ready, /convert-to-rental | `complete` | `PREP_PERMS.complete` |
 * | POST /{id}/cancel | any(`edit`, `create`, `complete`) | `PREP_CANCEL_PERMS` |
 *
 * จุดที่คนอ่านโค้ดมักเดาผิด:
 * - `accept` (รับงานเอง) ใช้ **`.assign`** ไม่ใช่ `.edit` — เพราะเป็นการผูกผู้รับผิดชอบ
 * - `cancel` เป็น OR 3 ตัว ไม่ใช่ `.edit` อย่างเดียว (ดู `PREP_CANCEL_PERMS`)
 * - **หน้ารายละเอียดใบงานใบเดียวมี 3 gate คนละตัว** — แก้หัวใบ (OR) / จับคู่อุปกรณ์ (`edit`) /
 *   ลบใบงาน (`edit`) · ถ้าเผลอใช้ตัวเดียวคุมทั้งหน้า จะได้ปุ่มที่กดแล้ว 403 หรือปุ่มที่หายไปทั้งที่มีสิทธิ์
 */
export const PREP_PERMS = {
  view: 'preparation_jobs.view',
  create: 'preparation_jobs.create',
  edit: 'preparation_jobs.edit',
  assign: 'preparation_jobs.assign',
  /** POST /{id}/accept — require_permission("preparation_jobs.assign") */
  accept: 'preparation_jobs.assign',
  /** POST /{id}/ready และ /convert-to-rental */
  complete: 'preparation_jobs.complete',
  export: 'preparation_jobs.export',
} as const;

/**
 * POST /{id}/cancel — backend `CancelGate = require_any_permission(edit, create, complete)`
 * → FE เช็คแบบ OR ให้ครบทั้ง 3
 *
 * **เคยหลุด (BUG-UI-06):** ตอนแรก backend เป็น any(edit, create) · พอ backend เพิ่ม `complete`
 * ให้ Manager ยกเลิกได้ ฝั่ง FE ไม่ได้เพิ่มตาม → backend ตอบ 200 แต่ปุ่มถูกซ่อนบนจอ
 * = ปัญหาที่แก้ที่ backend แล้ว "ย้ายมาโผล่ที่ frontend แทน"
 * **แก้ที่นี่ที่เดียวเสมอ** (ใช้ทั้ง PreparationJobViewPage และ ReservedItemsPanel)
 */
export const PREP_CANCEL_PERMS: readonly string[] = [
  'preparation_jobs.edit',
  'preparation_jobs.create',
  'preparation_jobs.complete',
];

/**
 * `PUT /{id}` (แก้หัวใบ: ลูกค้า/สถานที่/วันที่ต้องการ/ราคา/ผู้ติดต่อ/หมายเหตุ/ความเร่งด่วน)
 * — backend `EditHeaderGate = require_any_permission(edit, create)`
 *
 * **ทำไมต้องแยกจาก `PREP_PERMS.edit`:** บน prod เซลมีแค่ `view` + `create` (ไม่มี `edit`)
 * ซึ่งเป็นคนเปิดใบเองและเป็นคนเดียวที่รู้ว่าลูกค้าเปลี่ยนวันที่ — ถ้าใช้ `edit` คุม
 * ปุ่ม "แก้ไขข้อมูลใบงาน" จะหายจากจอของคนที่ backend อนุญาตจริง (ใบที่พิมพ์ชื่อลูกค้าผิด
 * จะแก้ไม่ได้เลยจนกว่าจะรบกวน Support)
 *
 * ⚠️ **ห้ามเอาไปคุม `PUT /{id}/items` หรือ `DELETE /{id}`** — สองเส้นนั้นยังเป็น `edit` ล้วน
 * (เซลจับคู่อุปกรณ์/ลบใบงานไม่ได้) · ใช้ `PREP_PERMS.edit` กับสองปุ่มนั้นเสมอ
 */
export const PREP_EDIT_HEAD_PERMS: readonly string[] = [
  'preparation_jobs.edit',
  'preparation_jobs.create',
];

// ========== สถานะ / ความเร่งด่วน ==========

export type PreparationJobStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'READY'
  | 'DELIVERED'
  | 'CANCELLED';

export type PreparationJobPriority = 'NORMAL' | 'HIGH' | 'URGENT';

/**
 * ค่าคงที่สำรอง — ใช้เมื่อ `GET /preparation-jobs/options` ยังไม่ตอบ หรือเป็น fallback
 * ของ `status_label`/`priority_label` ที่ backend ส่งมาในทุกแถวอยู่แล้ว
 * **ห้ามใช้แทนค่าจาก API ถ้ามีค่าจาก API แล้ว**
 */
export const PREPARATION_JOB_STATUSES: ReadonlyArray<{
  value: PreparationJobStatus;
  label: string;
}> = [
  { value: 'REQUESTED', label: 'รอเตรียม' },
  { value: 'IN_PROGRESS', label: 'กำลังเตรียม' },
  { value: 'READY', label: 'พร้อมส่งมอบ' },
  { value: 'DELIVERED', label: 'ส่งมอบแล้ว' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
];

export const PREPARATION_JOB_PRIORITIES: ReadonlyArray<{
  value: PreparationJobPriority;
  label: string;
}> = [
  { value: 'NORMAL', label: 'ปกติ' },
  { value: 'HIGH', label: 'ด่วน' },
  { value: 'URGENT', label: 'ด่วนมาก' },
];

/** สถานะที่ใบยังเดินอยู่ (แก้หัวใบ/จับคู่อุปกรณ์/ยกเลิกได้) */
export const OPEN_JOB_STATUSES: ReadonlyArray<PreparationJobStatus> = [
  'REQUESTED',
  'IN_PROGRESS',
  'READY',
];

export const statusLabel = (value: string | null | undefined): string => {
  if (!value) return '-';
  return PREPARATION_JOB_STATUSES.find((o) => o.value === value)?.label ?? value;
};

export const priorityLabel = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return PREPARATION_JOB_PRIORITIES.find((o) => o.value === value)?.label ?? value;
};

export interface EnumOption {
  value: string;
  label: string;
}

/** GET /preparation-jobs/options — แหล่งความจริงของคำไทย */
export interface PreparationJobOptionsResponse {
  statuses: EnumOption[];
  priorities: EnumOption[];
}

// ========== Rows (mirror schemas/preparation_job.py) ==========

export interface PreparationJobItemRow {
  id: number;
  product_id: number;
  product_name: string | null;
  quantity: number;

  /** จับคู่แล้ว (Support ทำในหน้ารายละเอียด) — ว่าง = ยังไม่จับคู่ */
  device_id: number | null;
  device_code: string | null;
  device_serial: string | null;
  device_is_serialized: boolean | null;
  sim_id: number | null;
  sim_phone: string | null;

  /** ทีมเตรียมเครื่องติ๊กว่าจัดของแถวนี้เสร็จแล้ว */
  is_prepared: boolean;
  /** ระบบจองเครื่อง/ซิมให้ใบนี้แล้ว (ของถูกกันไว้ ไม่ให้ใบอื่นหยิบ) */
  device_reserved: boolean;
  sim_reserved: boolean;

  /**
   * เครื่องที่จับคู่ไว้มีใบงานซ่อมค้าง (OPEN/PENDING/FOLLOW_UP) — backend คำนวณให้
   * มีเฉพาะใน `GET /{id}` (รายการในหน้า list ไม่ได้ join มา) จึง default false
   */
  has_open_maintenance?: boolean;

  /**
   * ราคา/เดือน **ต่อหน่วย** ตามสินค้าของแถวนี้ (feedback ทิวากร 18 ก.ย. 2026 —
   * 1 ใบมีหลายสินค้า ราคาหัวใบเดียวแยกไม่ออกว่าเป็นของสินค้าไหน) · null = ไม่ได้ระบุ
   */
  monthly_price: number | null;
  /** ราคาตั้งต้นของสินค้า (products.monthly_rental_rate) — ใช้เป็น placeholder ในฟอร์มเท่านั้น ห้าม prefill */
  product_default_price: number | null;

  /**
   * จับคู่ครบตามกติกา backend — **ใช้แทน `!!device_id` ทุกที่ที่นับ "จับคู่แล้ว"**
   * (แถวจำนวน >1 ที่ผูกเครื่องมี S/N ไว้เครื่องเดียว = false เพราะยังไม่ครบจำนวน)
   */
  is_matched: boolean;

  /** สัญญาที่รายการนี้ถูกแปลงไป (หลัง convert-to-rental · 1 เครื่อง = 1 สัญญา — แถวละสัญญา) */
  rental_id: number | null;
  rental_number: string | null;
  rental_status: string | null;

  /**
   * AnyDesk ของเครื่องในแถวนี้ (23 ก.ย. 2026 · feedback Tirawat) — Support จดตอนเตรียมเครื่อง
   * แล้วติดไปกับรายการเช่าตอน convert-to-rental · null = ยังไม่กรอก (backend อาจยังไม่ส่ง key = undefined)
   */
  anydesk_id?: string | null;

  notes: string | null;
}

export interface PreparationJobRow {
  id: number;
  job_number: string;

  status: PreparationJobStatus | string;
  status_label: string;
  priority: PreparationJobPriority | string | null;
  priority_label: string | null;

  /** ลูกค้า: เลือกจาก master (company_id) **หรือ** พิมพ์ชื่อใหม่ (customer_name_text) */
  company_id: number | null;
  company_name: string | null;
  customer_name_text: string | null;
  /** ชื่อที่เอาไปโชว์ได้เลย — backend ประกอบให้แล้ว (บริษัทจริง > ข้อความที่พิมพ์) */
  customer_display: string;

  /** สถานที่: เลือกจาก master (site_id) **หรือ** พิมพ์อิสระ (site_name_text) */
  site_id: number | null;
  site_name: string | null;
  site_name_text: string | null;
  site_display: string;

  needed_date: string;
  /** เลยวันที่ลูกค้าอยากได้ของแล้วแต่ยังไม่ส่งมอบ — **backend คำนวณ FE ห้ามคำนวณเอง** */
  is_overdue: boolean;
  /** เลยกำหนดมากี่วัน (0 = ยังไม่เลย) — backend คำนวณให้ ไม่ต้องเอา days_until_needed มาคิดเอง */
  days_overdue: number;
  /** ติดลบ = เลยกำหนดมาแล้วกี่วัน · 0 = ครบกำหนดวันนี้ */
  days_until_needed: number;

  /**
   * ราคารวมแบบเดิม (legacy — หัวใบเดียวไม่แยกสินค้า) — ใบเก่าบางใบมีค่านี้แต่ไม่มีราคารายรายการ
   * ใบใหม่ที่กรอกราคาแยกรายสินค้าแล้วฟิลด์นี้จะว่าง — ใช้ `monthly_price_total`/`has_item_prices` แทน
   */
  monthly_price: number | null;
  /** ผลรวม (ราคาต่อหน่วย × จำนวน) ของรายการที่มีราคา · ไม่มีรายการไหนมีราคา = ค่า legacy หัวใบ */
  monthly_price_total: number | null;
  /** มีรายการที่ระบุราคาอย่างน้อย 1 แถว */
  has_item_prices: boolean;
  contact_name: string | null;
  contact_phone: string | null;
  notes: string | null;

  requested_by_user_id: number | null;
  requested_by_name: string | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;

  accepted_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;

  /** ใบเสนอราคาต้นทาง (เปิดใบผ่าน POST /quotations/{id}/preparation-job) — null = เปิดจากคิวงานโดยตรง */
  quotation_id: number | null;
  quotation_number: string | null;

  /** สัญญาแรกที่ใบนี้แปลงไป (ความเข้ากันได้กับ FE เก่า) — ดู `rentals` สำหรับทุกสัญญา */
  rental_id: number | null;
  rental_number: string | null;

  /**
   * ทุกสัญญาที่ใบนี้แปลงไป (เรียงตามลำดับสร้าง) — **1 เครื่อง = 1 สัญญา** (มติ 10 ต.ค. 2026 แทน
   * "1 สินค้า = 1 สัญญา" เดิม) ใบที่มี K-Park 2 เครื่องจะได้ 2 ใบ สินค้าเดียวกัน → แยกด้วย `device_labels`
   */
  rentals: Array<{
    rental_id: number;
    rental_number: string;
    rental_status: string;
    product_names: string[];
    /** ป้ายเครื่องในสัญญานี้ เช่น ["DEV172 · VA08263HJ1948"] — backend เก่าอาจยังไม่ส่ง (undefined) */
    device_labels?: string[];
  }>;

  /**
   * preview "แปลงแล้วจะได้สัญญากี่ใบ แต่ละใบเป็นเครื่องอะไร" — backend คำนวณด้วย helper เดียวกับ
   * ตอน convert จริง (`POST /convert-to-rental`) ดังนั้น **ห้าม FE จัดกลุ่มเอง** ไม่งั้นตัวเลข preview
   * กับตัวเลขที่ convert จริงอาจไม่ตรงกัน
   *
   * **1 เครื่อง = 1 สัญญา (มติ 10 ต.ค. 2026):** 1 entry = 1 แถวรายการของใบงาน (`item_id` ไม่ซ้ำ ·
   * `product_id` ซ้ำได้ — ห้ามใช้เป็น React key) · `item_count` = 1 เสมอ · `total_quantity` = จำนวนของแถว
   */
  convert_groups: Array<{
    item_id: number;
    product_id: number;
    product_name: string;
    device_code: string | null;
    device_serial: string | null;
    sim_phone: string | null;
    item_count: number;
    total_quantity: number;
    monthly_total: number | null;
    missing_price_count: number;
  }>;

  total_quantity: number;
  /** จำนวน "แถว" ที่จับคู่อุปกรณ์แล้ว */
  matched_quantity: number;
  /** แถวที่ยังไม่จับคู่ — ต้องเป็น 0 ก่อนกด "พร้อมส่งมอบ" */
  unmatched_count: number;

  created_at: string | null;
  updated_at: string | null;

  items: PreparationJobItemRow[];
}

/** แถวใน timeline ของหน้ารายละเอียด */
export interface PreparationJobActivityRow {
  id: number;
  action: string;
  description: string | null;
  user_id: number | null;
  user_name: string | null;
  created_at: string | null;
}

/**
 * รหัสคำเตือน — **ตัดสินใจจาก `code` เท่านั้น ห้าม match ข้อความไทย**
 * (ข้อความเป็นของ backend และแก้เมื่อไรก็ได้ · เคยพังมาแล้วในโปรเจกต์นี้ตอนจับคู่ด้วยข้อความ)
 *
 * 4 ตัวแรกเท่านั้นที่โผล่ใน `active_warnings` (เงื่อนไขที่ยัง "จริง" อยู่ตอนเปิดหน้า)
 * ที่เหลือเป็นผลของ action ครั้งนั้น ๆ จึงมาทาง `warning_details` ของ response ปุ่ม
 */
export type PreparationWarningCode =
  | 'DEVICE_HAS_OPEN_MAINTENANCE'
  | 'DEVICE_STATUS_CHANGED'
  | 'SIM_STATUS_CHANGED'
  | 'LOW_STOCK'
  | 'ITEM_NOT_MATCHED'
  | 'ITEM_NOT_PREPARED'
  | 'RENTAL_CREATED_AS_DRAFT'
  | 'PRICE_SPREAD'
  | 'RELEASE_SKIPPED';

/** 1 คำเตือนแบบมีโครงสร้าง (`PreparationWarning` ฝั่ง backend) */
export interface PreparationWarning {
  code: PreparationWarningCode | string;
  /** ข้อความไทยพร้อมแสดง — แหล่งความจริงอยู่ที่ backend จอไม่ประกอบเอง */
  message: string;
  severity: 'warning' | 'info' | string;
  item_id?: number | null;
  device_id?: number | null;
  device_code?: string | null;
  sim_id?: number | null;
  sim_phone?: string | null;
  /**
   * ส่งมาเสมอเมื่อคำเตือนอ้างถึงเคสซ่อม **ไม่ได้กรองตามสิทธิ์ผู้เรียก**
   * — FE เป็นคนตัดสินว่าจะทำเป็นลิงก์ไหม (ต้องมี `maintenance.view` เดี่ยว ๆ)
   */
  maintenance_id?: number | null;
}

/**
 * อุปกรณ์เสริมที่ส่งไปด้วย (ของนับจำนวน — สายชาร์จ/หัวชาร์จ/กระดาษ/เคส/ฟิล์ม) — feedback Support
 * "อยากบันทึกของที่ไม่มีซีเรียลได้ด้วย และตัดสต็อกได้เลย" (มติเจ้าของโปรเจกต์ 23 ก.ย. 2026)
 *
 * **ตัดยอดคลังทันทีที่บันทึก ไม่ใช่การ "จอง"** — ต่างจากเครื่อง/ซิมในรายการหลักที่ใช้สถานะ RESERVED
 * (ดู `PreparationJobItemRow`) เพราะของนับจำนวนไม่มีสถานะ (ดู CLAUDE.md "ของนับจำนวนไม่ควรมีสถานะ")
 * ความจริงอยู่ที่ `quantity` เท่านั้น การบันทึก/แก้/ลบแถวนี้จึงเดินผ่าน `stock_service` ฝั่ง backend
 * เขียน ledger ให้ทุกครั้ง — ไม่ใช่การถือของค้างแบบเดียวกับ `device_reserved`/`sim_reserved`
 *
 * **ห้ามมีช่องราคา** — มติ PO: ของกลุ่มนี้ไม่คิดเงิน (ต่างจาก `PreparationJobItemRow.monthly_price`)
 */
export interface PreparationJobAccessoryRow {
  id: number;
  device_id: number;
  device_code: string | null;
  /** ป้ายแสดงผลที่ backend ประกอบแล้ว (ประเภท+ยี่ห้อ+รุ่น) — ใช้ตรง ๆ เหมือน `device_name` ของ picker */
  device_name: string | null;
  unit: string | null;
  quantity: number;
  notes: string | null;
  /** ยอดคงเหลือของอุปกรณ์นี้ ณ ตอนที่เรียก `GET /{id}` (snapshot สด ไม่ใช่ค่าคงที่) */
  current_stock: number;
  issued_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  /**
   * ใบถูกยกเลิก/ลบก่อนส่งมอบ → ระบบคืนยอดแถวนี้เข้าคลังให้เองแล้วประทับเวลาไว้ที่นี่
   * มีค่า = แถว "ประวัติ" เท่านั้น ห้ามให้แก้/ลบซ้ำ (ยอดคืนไปแล้ว)
   */
  returned_to_stock_at: string | null;
}

export interface PreparationJobDetail extends PreparationJobRow {
  timeline: PreparationJobActivityRow[];
  /** ข้อความล้วนของชุดเดียวกับ `active_warnings` (backend derive มาจากก้อนเดียวกัน) */
  warnings?: string[];
  /**
   * **คำนวณสดทุกครั้งที่เรียก `GET /{id}`** = "เงื่อนไขที่ยังจริงตอนนี้"
   * ต่างจาก `warnings` ของ action ที่แปลว่า "ผลของสิ่งที่เพิ่งกด"
   *
   * เพราะมากับตัวใบงาน แถบคำเตือนบนจอจึงรอด reload และ**หายเองเมื่อต้นเหตุถูกแก้**
   * — ไม่ต้องเก็บ state/localStorage ที่จะกลายเป็น "คำเตือนค้างจากเมื่อวาน" ที่อาจไม่จริงแล้ว
   * · ว่างเสมอสำหรับใบที่ส่งมอบ/ยกเลิกแล้ว
   */
  active_warnings?: PreparationWarning[];
  /** อุปกรณ์เสริม (ของนับจำนวน) ที่ส่งไปพร้อมใบนี้ — optional กัน deploy skew (backend เก่ายังไม่ส่ง) */
  accessories?: PreparationJobAccessoryRow[];
  /** ใบยังแก้อุปกรณ์เสริมได้ไหม (ยังไม่ส่งมอบ/ไม่ยกเลิก/ไม่ถูกลบ) — ใช้แทนการคำนวณเองจาก status */
  accessories_editable?: boolean;
}

export interface PreparationJobListResponse {
  items: PreparationJobRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface PreparationJobStats {
  requested: number;
  in_progress: number;
  ready: number;
  delivered: number;
  cancelled: number;
  /** ยังไม่ส่งมอบ + เลยวันที่ลูกค้าอยากได้ของ */
  overdue: number;
  assigned_to_me: number;
  /** REQUESTED + IN_PROGRESS + READY */
  total_open: number;
  /** ของที่ถูกกันไว้อยู่ตอนนี้ — สัญญาร่างคงการจองได้ไม่จำกัดเวลา จึงต้องเห็นยอดค้าง */
  reserved_devices: number;
  reserved_sims: number;
  /**
   * จำนวน "รายการ (แถว)" ที่ยังถือของอยู่ — backend ยังไม่ส่ง (8 ก.ย. 2026) FE ใช้
   * `summary.total_items` จาก /reserved-items แทน · ถ้า backend เพิ่มฟิลด์นี้ FE จะใช้ทันที
   */
  reserved_items?: number;
  /** รายการที่จองไว้นานที่สุดกี่วัน */
  oldest_reservation_days: number;
}

/**
 * **ทุก action ตอบรูปแบบนี้** — create / update / items / accept / assign / ready / cancel / delete
 * (ยกเว้น convert-to-rental ที่มีฟิลด์เพิ่ม) · `job` = ก้อนเดียวกับ GET /{id} มี timeline ครบ
 *
 * อย่าอ่าน field ของใบงานจาก response ตรง ๆ (`res.job_number`) — ต้องผ่าน `res.job`
 */
export interface PreparationJobActionResponse {
  job: PreparationJobDetail;
  /** เตือนแต่ไม่บล็อค เช่น ของนับจำนวนยอดไม่พอ, สร้างสัญญาเป็นร่างเพราะไม่มีสิทธิ์อนุมัติ */
  warnings: string[];
  /** ข้อมูลชุดเดียวกับ `warnings` แบบมีโครงสร้าง (ใช้เมื่อต้องการ `code`/เลขเคสซ่อม) */
  warning_details?: PreparationWarning[];
}

export interface ConvertToRentalResponse {
  job: PreparationJobDetail;
  /** สัญญาแรกที่ถูกสร้าง (ความเข้ากันได้กับโค้ดเก่า) — ดู `rentals` สำหรับสัญญาทั้งหมด */
  rental_id: number;
  rental_number: string;
  /** ผู้กดมี `rentals.approve` → ACTIVE, ไม่มี → DRAFT */
  rental_status: string;
  /** **1 เครื่อง = 1 สัญญา** — 1 แถวรายการของใบงาน = 1 สัญญา เรียงตามลำดับที่สร้าง */
  rentals: Array<{
    rental_id: number;
    rental_number: string;
    rental_status: string;
    /** แถวรายการของใบงานที่ได้สัญญาใบนี้ + ป้ายเครื่อง (nullable — backend เก่าอาจไม่ส่ง) */
    item_id?: number | null;
    device_code?: string | null;
    device_serial?: string | null;
    sim_phone?: string | null;
    product_name: string;
    item_count: number;
    total_quantity: number;
    monthly_total: number | null;
  }>;
  warnings: string[];
  warning_details?: PreparationWarning[];
  /** ข้อความสรุปพร้อมเลขสัญญาทุกใบ — ใช้แสดงหลังสำเร็จได้ตรง ๆ */
  message: string;
}

/** ตัวเลือกสินค้าสำหรับจอเปิดใบ (GET /products — gate ด้วย preparation_jobs.view) */
export interface PreparationProductOption {
  id: number;
  name: string;
  code?: string | null;
  /** products.monthly_rental_rate — ใช้เป็น placeholder ราคา/เดือนต่อแถวในฟอร์มเปิดใบเท่านั้น ห้าม prefill */
  monthly_rental_rate: number | null;
}

/** แถวใน picker อุปกรณ์ (GET /available-devices) */
export interface AvailableDeviceRow {
  id: number;
  code: string | null;
  serial_number: string | null;
  is_serialized: boolean;
  quantity: number;
  unit: string | null;
  status: string | null;
  status_label: string | null;
  device_type_name: string | null;
  model_name: string | null;
  /** ประเภท + ยี่ห้อ + รุ่น (backend ประกอบให้ · optional กัน deploy skew) */
  device_name?: string | null;
}

/**
 * ป้ายใน picker เครื่องของใบงาน
 * - มี S/N: "รหัส · ประเภท ยี่ห้อ รุ่น · S/N" (รูปเดียวกับ `itemDeviceLabel` ของแถวที่บันทึกแล้ว แค่มีชื่อแทรก)
 * - นับจำนวน: "รหัส · ประเภท ยี่ห้อ รุ่น — คงเหลือ N หน่วย"
 * feedback ทิวากร 18 ก.ย. 2026: เดิม "รหัส · S/N" เท่านั้น ของนับจำนวน (ไม่มี S/N) ขึ้นแค่ "DEV178"
 */
export const availableDeviceLabel = (d: AvailableDeviceRow): string => {
  const kind = d.device_name ?? [d.device_type_name, d.model_name].filter(Boolean).join(' ');
  if (d.is_serialized) {
    return [d.code, kind, d.serial_number].filter(Boolean).join(' · ') || `เครื่อง #${d.id}`;
  }
  const head = [d.code, kind].filter(Boolean).join(' · ') || `เครื่อง #${d.id}`;
  return `${head} — คงเหลือ ${d.quantity} ${d.unit || 'ชิ้น'}`;
};

/**
 * ป้ายใน picker อุปกรณ์เสริม — "รหัส · ชื่อ — คงเหลือ N หน่วย" หรือ "รหัส · ชื่อ — หมด" เมื่อ quantity=0
 * (ห้ามซ่อนของหมดสต็อกออกจาก picker — แสดงแล้วให้ผู้ใช้เห็นว่าทำไมเลือกไม่ได้)
 */
export const accessoryOptionLabel = (a: AvailableAccessoryRow): string => {
  const head = [a.code, a.name].filter(Boolean).join(' · ') || `อุปกรณ์ #${a.id}`;
  if (a.quantity <= 0) return `${head} — หมด`;
  return `${head} — คงเหลือ ${a.quantity} ${a.unit || 'ชิ้น'}`;
};

/** ชื่อ/รหัสของแถวอุปกรณ์เสริมที่บันทึกแล้ว (snapshot จาก backend) */
export const accessoryRowLabel = (row: {
  device_code?: string | null;
  device_name?: string | null;
  device_id?: number | null;
}): string =>
  [row.device_code, row.device_name].filter(Boolean).join(' · ') ||
  (row.device_id ? `อุปกรณ์ #${row.device_id}` : '-');

/** แถวใน picker ซิม (GET /available-sims) */
export interface AvailableSimRow {
  id: number;
  phone_number: string | null;
  status: string | null;
  status_label: string | null;
}

export interface AvailableDevicesResponse {
  items: AvailableDeviceRow[];
  total: number;
  page: number;
  page_size: number;
  // ค-2 (fix ค, 20 ก.ย. 2026): แถวที่ "พบแต่ใช้ไม่ได้" — เติมเฉพาะตอน search ไม่ว่าง + หน้าแรก
  // สูงสุด 5 แถว · optional เพราะ backend รุ่นเก่ายังไม่ส่ง → ต้องอ่านว่า "ไม่มี" ไม่ใช่ error
  blocked?: PickerBlockedItem[];
}

export interface AvailableSimsResponse {
  items: AvailableSimRow[];
  total: number;
  page: number;
  page_size: number;
  blocked?: PickerBlockedItem[];
}

/**
 * แถวใน picker อุปกรณ์เสริม (GET /available-accessories) — ของนับจำนวนทุกชิ้นที่ไม่ถูกลบ
 * **รวมตัวที่ `quantity=0` ด้วย** (backend ไม่กรองออก — จอเป็นคนแสดง "หมด" ไม่ใช่ซ่อนทิ้ง เพราะของ
 * ที่หายจาก picker เงียบ ๆ เคยทำให้ผู้ใช้งงมาแล้วในโมดูลอื่นของระบบนี้)
 */
export interface AvailableAccessoryRow {
  id: number;
  code: string | null;
  /** ป้ายแสดงผลที่ backend ประกอบแล้ว (ประเภท+ยี่ห้อ+รุ่น) — ใช้ตรง ๆ */
  name: string;
  unit: string | null;
  quantity: number;
  minimum_quantity: number | null;
  low_stock: boolean;
}

export interface AvailableAccessoriesResponse {
  items: AvailableAccessoryRow[];
}

/** แถวใน "ของค้างจอง" (GET /reserved-items) */
export interface ReservedItemRow {
  item_id: number;
  job_id: number;
  job_number: string;
  job_status: string;
  job_status_label: string;
  customer_display: string;
  product_name: string | null;
  device_id: number | null;
  device_code: string | null;
  sim_id: number | null;
  sim_phone: string | null;
  /** มีค่าเมื่อใบงานถูกแปลงเป็นสัญญาแล้วแต่ของยังถูกจองอยู่ (สัญญาร่างที่ยังไม่อนุมัติ) */
  rental_id: number | null;
  rental_number: string | null;
  reserved_since: string | null;
  days_reserved: number;
  /** optional — ขอ backend เพิ่มแล้ว ยังไม่มาก็แสดง "—" (ไม่ทำให้จอพัง) */
  assigned_to_name?: string | null;
  /** เครื่องที่ค้างจองใบนี้มีเคสซ่อมค้างอยู่ด้วย — ของที่ไม่ควรปล่อยไปให้ลูกค้า */
  has_open_maintenance?: boolean;
}

export interface ReservedItemsResponse {
  items: ReservedItemRow[];
  total: number;
  page: number;
  page_size: number;
  /** **คิดจากทั้งชุดเสมอ ไม่ใช่เฉพาะหน้าที่กำลังดู** — ตัวเลขหัวจอจะได้ไม่กระโดดตามหน้า */
  summary: {
    total_items: number;
    devices: number;
    sims: number;
    oldest_days: number;
  };
}

// ========== Helpers แสดงผล ==========

/** ชื่อลูกค้าที่โชว์บนจอ — ใช้ค่าที่ backend ประกอบมาก่อนเสมอ */
export const customerDisplay = (row: {
  customer_display?: string | null;
  company_name?: string | null;
  customer_name_text?: string | null;
}): string =>
  row.customer_display || row.company_name || row.customer_name_text || 'ไม่ระบุลูกค้า';

/** ชื่อสถานที่/โครงการที่โชว์บนจอ (กติกาเดียวกับ `customerDisplay`) */
export const siteDisplay = (row: {
  site_display?: string | null;
  site_name?: string | null;
  site_name_text?: string | null;
}): string => row.site_display || row.site_name || row.site_name_text || 'ไม่ระบุสถานที่';

/**
 * ลูกค้า/สถานที่ยังเป็นชื่อที่พิมพ์เอง (ไม่มีใน master)
 * — ใช้ติดป้ายเตือน Support เพราะ **แปลงเป็นสัญญาเช่าไม่ได้จนกว่าจะผูกของจริง** (backend 400)
 */
export const isFreeTextCustomer = (row: { company_id?: number | null }): boolean => !row.company_id;

export const isFreeTextSite = (row: { site_id?: number | null }): boolean => !row.site_id;

/** ใบนี้พร้อมแปลงเป็นสัญญาเช่าไหม (ต้องผูกบริษัท+โครงการจริงก่อน) */
export const canConvertToRental = (row: {
  company_id?: number | null;
  site_id?: number | null;
}): boolean => !!row.company_id && !!row.site_id;

/**
 * จับคู่ครบแล้วหรือยัง — **ใช้ `is_matched` เสมอ ห้ามกลับไปเช็ค `!!device_id`**
 * (แถวจำนวน >1 ที่ผูกเครื่องมี S/N ไว้เครื่องเดียวนับว่ายังไม่ครบ) · fallback ให้ของเก่าที่ backend
 * ยังไม่ส่ง field นี้มา (deploy skew) ไม่ให้จอพัง
 */
export const isItemMatched = (item: PreparationJobItemRow): boolean =>
  typeof item.is_matched === 'boolean' ? item.is_matched : !!item.device_id;

/** รายการที่ยังจับคู่อุปกรณ์ไม่ได้ */
export const unmatchedItems = (job: {
  items?: PreparationJobItemRow[] | null;
}): PreparationJobItemRow[] => (job.items || []).filter((it) => !isItemMatched(it));

/** ชื่อสินค้าแบบสั้นสำหรับตาราง */
export const itemLabel = (item: PreparationJobItemRow): string =>
  item.product_name || `สินค้า #${item.product_id}`;

/** จัดรูปแบบราคา/เดือนเป็นข้อความไทย เช่น "1,800.00 บาท" — คืน null ถ้าไม่มีค่า (ไม่บวกหน่วยต่อท้ายให้ผู้เรียกใส่เอง) */
export const formatMonthlyPrice = (value: number | null | undefined): string | null =>
  typeof value === 'number'
    ? value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' บาท'
    : null;

/** ชื่อเครื่องที่จับคู่ไว้ (snapshot มาก่อนเสมอ) */
export const itemDeviceLabel = (item: PreparationJobItemRow): string | null => {
  const label = [item.device_code, item.device_serial].filter(Boolean).join(' · ');
  return label || (item.device_id ? `เครื่อง #${item.device_id}` : null);
};

export const itemSimLabel = (item: PreparationJobItemRow): string | null =>
  item.sim_phone || (item.sim_id ? `ซิม #${item.sim_id}` : null);

/** สรุปสินค้าในใบแบบบรรทัดเดียว เช่น "K-Visit x2 · K-Scan x1" */
export const itemsSummary = (
  items: PreparationJobItemRow[] | null | undefined,
  max = 3
): string => {
  const list = items || [];
  if (list.length === 0) return '-';
  const parts = list.slice(0, max).map((it) => `${itemLabel(it)} x${it.quantity}`);
  if (list.length > max) parts.push(`+อีก ${list.length - max}`);
  return parts.join(' · ');
};

/** ใบที่ปิดแล้ว (ส่งมอบ/ยกเลิก) — ไม่มี "เหลือ/เกินกี่วัน" อีก เพราะไม่มีอะไรต้องทำ */
export const PREP_TERMINAL_STATUSES: ReadonlyArray<PreparationJobStatus> = ['DELIVERED', 'CANCELLED'];

export const isPrepJobTerminal = (status?: string | null): boolean =>
  !!status && (PREP_TERMINAL_STATUSES as ReadonlyArray<string>).includes(status);

/**
 * ข้อความ "เหลือ/เกินกี่วัน" จาก `days_until_needed` ที่ backend คำนวณให้
 * (ติดลบ = เลยกำหนด · 0 = วันนี้) — FE ห้ามคิดวันเอง
 *
 * R2U-05 (carry-over รอบ 2, แก้ 8 ก.ย. 2026): ใบที่ยกเลิก/ส่งมอบแล้ว backend ส่ง `is_overdue=false`
 * และ `days_overdue=0` แต่ `days_until_needed` ยังติดลบตามวันจริง → ป้ายวันที่เคยขึ้น
 * "เกินกำหนด 3 วัน" ให้ใบที่ยกเลิกไปแล้ว · ใบปิดแล้วไม่มีนับถอยหลังทุกกรณี
 */
export const neededDateHint = (row: {
  is_overdue?: boolean;
  days_until_needed?: number | null;
  status?: string | null;
}): string | null => {
  if (isPrepJobTerminal(row.status)) return null;
  const days = row.days_until_needed;
  if (typeof days !== 'number') return null;
  if (days < 0) return `เกินกำหนด ${Math.abs(days)} วัน`;
  if (days === 0) return 'ครบกำหนดวันนี้';
  return `เหลือ ${days} วัน`;
};

// ========== Payloads ==========

export interface PreparationJobItemInput {
  product_id: number;
  quantity: number;
  device_id?: number | null;
  sim_id?: number | null;
  /** ราคา/เดือน **ต่อหน่วย** ที่เซลแจ้ง — ไม่ระบุ/null = ไม่มีราคา (ใช้เลขจากหน้าสัญญาแทนภายหลัง) */
  monthly_price?: number | null;
  notes?: string | null;
}

/**
 * body ของ POST / — 7 ช่องตามที่ PO กำหนด (บังคับ 4)
 * `monthly_price` (หัวใบ) เหลือไว้เพื่อความเข้ากันได้กับ backend เท่านั้น —
 * **ฟอร์มเปิดใบตัวใหม่ไม่ส่งค่านี้แล้ว** ราคาย้ายลงเป็นต่อแถวสินค้าที่ `items[].monthly_price`
 */
export interface PreparationJobCreateRequest {
  company_id?: number | null;
  customer_name_text?: string | null;
  site_id?: number | null;
  site_name_text?: string | null;
  needed_date: string;
  priority?: PreparationJobPriority | null;
  monthly_price?: number | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  items: PreparationJobItemInput[];
}

/** 1 แถวราคาที่จะแก้ใน PUT /{id} — ส่งเฉพาะแถวที่เปลี่ยน (ไม่ใช่ทุกแถวเหมือน PUT /items) */
export interface PreparationJobItemPriceUpdate {
  item_id: number;
  /** null = ล้างราคาที่เคยกรอกไว้ */
  monthly_price: number | null;
}

/**
 * แก้หัวใบ (PUT /{id}) — ฟิลด์ที่ไม่ส่ง = ไม่ถูกแตะ (bug class form-field-overwrite)
 * `monthly_price` เหลือไว้เพื่อความเข้ากันได้ — **ฟอร์มแก้ไขใบตัวใหม่ไม่ส่งค่านี้แล้ว** ใช้ `item_prices` แทน
 */
export type PreparationJobUpdateRequest = Partial<Omit<PreparationJobCreateRequest, 'items'>> & {
  item_prices?: PreparationJobItemPriceUpdate[];
};

/**
 * 1 แถวใน PUT /{id}/items — diff-based ตาม `id`
 * **แถวเดิมที่ไม่ส่งมาใน payload = ถูกลบ + ปล่อยของที่จองไว้** → ต้องส่งครบทุกแถวเสมอ
 *
 * ⚠️ **ห้ามส่ง key `monthly_price` จากจอจับคู่อุปกรณ์ (PreparationJobItemsPanel)** — endpoint นี้
 * ตีความ "ไม่ส่ง key" ว่า "คงค่าเดิม" ถ้าส่ง `undefined`/ไม่ใส่ key เลยปลอดภัย แต่ถ้าเผลอส่ง `null`
 * จะไปล้างราคาที่หน้าแก้ไขใบงานเพิ่งกรอกไว้เงียบ ๆ (bug class form-field-overwrite) — แก้ราคาที่
 * `PreparationJobEditModal` ผ่าน `item_prices` เท่านั้น
 */
export interface PreparationJobItemUpsert {
  id?: number | null;
  product_id: number;
  quantity: number;
  device_id?: number | null;
  sim_id?: number | null;
  is_prepared?: boolean;
  /**
   * AnyDesk ของเครื่องในแถวนี้ — **ไม่ส่ง key = คงค่าเดิม** · null/'' = ล้าง
   * จอจับคู่ส่งทุกแถว (ค่าจาก draft) · จออื่นที่เรียก updateItems ห้ามใส่ key นี้
   */
  anydesk_id?: string | null;
  /** มีไว้เพื่อตรงกับ backend schema เท่านั้น — `PreparationJobItemsPanel` ต้องไม่ใส่ key นี้ (ดูคำเตือนด้านบน) */
  monthly_price?: number | null;
  notes?: string | null;
}

export interface PreparationJobItemsUpdateRequest {
  items: PreparationJobItemUpsert[];
}

/**
 * 1 แถวใน PUT /{id}/accessories — **diff-based ตาม `id`** เหมือน `PreparationJobItemUpsert`:
 * แถวที่มี `id` = แก้ในที่ · ไม่มี `id` = แถวใหม่ · แถวเดิมที่ไม่ส่งมาในคำขอ = ถูกลบ + คืนยอดเข้าคลัง
 * ห้าม `device_id` ซ้ำกันในหลายแถวของ payload เดียวกัน (backend ตอบ 400)
 */
export interface PreparationJobAccessoryInput {
  id?: number;
  device_id: number;
  /** 1..10000 */
  quantity: number;
  notes?: string | null;
}

export interface PreparationJobAccessoriesUpdateRequest {
  accessories: PreparationJobAccessoryInput[];
}

export interface PreparationJobAssignRequest {
  user_id: number;
  note?: string | null;
}

export interface PreparationJobCancelRequest {
  reason: string;
}

/** แปลงเป็นสัญญาเช่า — ทุกช่อง optional ไม่ส่งก็ใช้ค่าจากใบงาน */
export interface ConvertToRentalRequest {
  site_id?: number | null;
  company_id?: number | null;
  rental_date?: string | null;
  billing_start_date?: string | null;
  expected_return_date?: string | null;
  monthly_price?: number | null;
  notes?: string | null;
}

// ========== Filters ==========

export interface PreparationJobFilters {
  page?: number;
  page_size?: number;
  status?: PreparationJobStatus | '';
  assigned_to_me?: boolean;
  assigned_to_user_id?: number;
  company_id?: number;
  site_id?: number;
  /** ใบงานที่แปลงเป็นรายการเช่านี้ (การ์ดบนหน้ารายละเอียดการเช่า) — backend รองรับ 2026-09-08 */
  rental_id?: number;
  overdue?: boolean;
  search?: string;
}

/** filter ชุดเดียวใช้ทั้ง list และ export (bug class export-filter-parity) */
const buildFilterParams = (
  filters: Omit<PreparationJobFilters, 'page' | 'page_size'>
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};
  if (filters.status) params.status = filters.status;
  if (filters.assigned_to_me) params.assigned_to_me = true;
  if (filters.assigned_to_user_id) params.assigned_to_user_id = filters.assigned_to_user_id;
  if (filters.company_id) params.company_id = filters.company_id;
  if (filters.site_id) params.site_id = filters.site_id;
  if (filters.rental_id) params.rental_id = filters.rental_id;
  if (filters.overdue) params.overdue = true;
  if (filters.search) params.search = filters.search;
  return params;
};

// ========== Service ==========

const BASE = '/preparation-jobs';

const preparationJobService = {
  /** คิวงาน + ตัวกรอง (backend เรียงด่วนที่สุด/ใกล้ครบกำหนดขึ้นก่อนให้แล้ว) */
  async getJobs(filters: PreparationJobFilters = {}): Promise<PreparationJobListResponse> {
    const params = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
      ...buildFilterParams(filters),
    };
    return ApiWithLogging.get<PreparationJobListResponse>(`${BASE}/`, { params });
  },

  /** ตัวเลขบนการ์ดนับ — aggregate ทั้งชุดฝั่ง backend ไม่ใช่จากหน้าที่กำลังดู */
  async getStats(): Promise<PreparationJobStats> {
    return ApiWithLogging.get<PreparationJobStats>(`${BASE}/stats`);
  },

  /** ตัวเลือก + label ไทย (สถานะ / ความเร่งด่วน) */
  async getOptions(): Promise<PreparationJobOptionsResponse> {
    return ApiWithLogging.get<PreparationJobOptionsResponse>(`${BASE}/options`);
  },

  async getJob(id: number): Promise<PreparationJobDetail> {
    return ApiWithLogging.get<PreparationJobDetail>(`${BASE}/${id}`);
  },

  /** เปิดใบงาน — ไม่มีขั้นอนุมัติ (201 + `{job, warnings}`) */
  async createJob(data: PreparationJobCreateRequest): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.post<PreparationJobActionResponse>(
      `${BASE}/`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'create_preparation_job',
        entityType: 'preparation_job',
      }
    );
  },

  /** แก้หัวใบ (ฟิลด์ที่ไม่ส่ง = ไม่ถูกแตะ) */
  async updateJob(
    id: number,
    data: PreparationJobUpdateRequest
  ): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.put<PreparationJobActionResponse>(
      `${BASE}/${id}`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'update_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /** จับคู่เครื่อง/ซิมรายบรรทัด — จองของทันที · **ต้องส่งครบทุกแถว** (แถวที่ขาด = ถูกลบ) */
  async updateItems(
    id: number,
    data: PreparationJobItemsUpdateRequest
  ): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.put<PreparationJobActionResponse>(
      `${BASE}/${id}/items`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'update_preparation_job_items',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /** รับงานเอง (REQUESTED → IN_PROGRESS + ผูกตัวเองเป็นผู้รับผิดชอบ) — ต้องมี `.assign` */
  async accept(id: number): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.post<PreparationJobActionResponse>(
      `${BASE}/${id}/accept`,
      {},
      {},
      {
        service: 'preparationJobService',
        action: 'accept_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  async assign(
    id: number,
    data: PreparationJobAssignRequest
  ): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.post<PreparationJobActionResponse>(
      `${BASE}/${id}/assign`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'assign_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /** พร้อมส่งมอบ — backend ตอบ 400 พร้อมบอกรายการที่ยังไม่จับคู่ ถ้าจับคู่ไม่ครบ */
  async markReady(id: number): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.post<PreparationJobActionResponse>(
      `${BASE}/${id}/ready`,
      {},
      {},
      {
        service: 'preparationJobService',
        action: 'ready_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /**
   * แปลงเป็นสัญญาเช่า — บริษัท/โครงการต้องเป็นของจริง (มี id) ไม่งั้น backend ตอบ 400
   * สถานะสัญญาที่ได้ขึ้นกับสิทธิ์ผู้กด: มี `rentals.approve` → ACTIVE, ไม่มี → DRAFT
   */
  async convertToRental(
    id: number,
    data: ConvertToRentalRequest = {}
  ): Promise<ConvertToRentalResponse> {
    return ApiWithLogging.post<ConvertToRentalResponse>(
      `${BASE}/${id}/convert-to-rental`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'convert_preparation_job_to_rental',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /**
   * ลบใบงาน (soft delete) — ต้องมี `preparation_jobs.edit`
   * ใช้กับใบที่ "เปิดผิด/ซ้ำ" · backend ปล่อยของที่จองไว้คืนคลังเสมอ
   * (ต่างจาก `cancel` ที่เป็นผลลัพธ์ทางธุรกิจและยังเห็นในรายงาน)
   */
  async deleteJob(id: number): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.delete<PreparationJobActionResponse>(
      `${BASE}/${id}`,
      {},
      {
        service: 'preparationJobService',
        action: 'delete_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  async cancel(
    id: number,
    data: PreparationJobCancelRequest
  ): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.post<PreparationJobActionResponse>(
      `${BASE}/${id}/cancel`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'cancel_preparation_job',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /**
   * สินค้าที่เปิดใบได้ — gate ด้วย `preparation_jobs.view` (ไม่ใช่ `products.view`)
   *
   * จุดสำคัญของ endpoint นี้: เซลบน prod **ไม่มี `products.view`** ถ้าฟอร์มไปดึง /products/
   * ตรง ๆ จะได้ 403 → dropdown ว่าง → เปิดใบไม่ได้เลย = ฟีเจอร์ตายกับผู้ใช้กลุ่มที่ทำมาเพื่อเขา
   */
  async getProductOptions(): Promise<PreparationProductOption[]> {
    const res = await ApiWithLogging.get<{ items: PreparationProductOption[] }>(
      `${BASE}/products`
    );
    return res?.items || [];
  },

  /**
   * อุปกรณ์ที่จับคู่เข้าใบงานได้
   *
   * **ต้องส่ง `jobId` เสมอตอนเปิดฟอร์มแก้ไข** — เครื่องที่ใบนี้จองไว้อยู่แล้วมีสถานะ RESERVED
   * (ไม่ใช่ AVAILABLE) ถ้าไม่ส่ง job_id มันจะหายจาก dropdown แล้วผู้ใช้กดบันทึกทับเป็นค่าว่าง
   * โดยไม่ตั้งใจ = ปล่อยของที่จองไว้คืนคลังเงียบ ๆ
   */
  async getAvailableDevices(
    jobId: number,
    options: { search?: string; page?: number; page_size?: number } = {}
  ): Promise<AvailableDevicesResponse> {
    const params: Record<string, string | number> = {
      job_id: jobId,
      page: options.page || 1,
      page_size: options.page_size || 50,
    };
    if (options.search) params.search = options.search;
    return ApiWithLogging.get<AvailableDevicesResponse>(`${BASE}/available-devices`, { params });
  },

  /** ซิมที่จับคู่เข้าใบงานได้ (กติกา `jobId` เหมือน `getAvailableDevices` ทุกประการ) */
  async getAvailableSims(
    jobId: number,
    options: { search?: string; page?: number; page_size?: number } = {}
  ): Promise<AvailableSimsResponse> {
    const params: Record<string, string | number> = {
      job_id: jobId,
      page: options.page || 1,
      page_size: options.page_size || 50,
    };
    if (options.search) params.search = options.search;
    return ApiWithLogging.get<AvailableSimsResponse>(`${BASE}/available-sims`, { params });
  },

  /**
   * อุปกรณ์เสริม (ของนับจำนวน) ที่ส่งไปพร้อมใบงานได้ — **รวมของที่ `quantity=0`** (จอเป็นคนแสดง
   * "หมด" แทนการซ่อน) ไม่ต้องส่ง `jobId` เสมอเหมือน `getAvailableDevices` เพราะของนับจำนวนไม่ถูก
   * "จอง" แบบเครื่อง/ซิม (ตัดยอดทันที ไม่มีสถานะ RESERVED ให้ต้องรวมกลับมาในผลค้นหา) — ส่งไปเมื่อมี
   * เพื่อความเข้ากันได้กับ backend เท่านั้น
   */
  async getAvailableAccessories(
    options: { search?: string; jobId?: number; limit?: number } = {}
  ): Promise<AvailableAccessoriesResponse> {
    const params: Record<string, string | number> = { limit: options.limit || 50 };
    if (options.search) params.search = options.search;
    if (options.jobId) params.job_id = options.jobId;
    return ApiWithLogging.get<AvailableAccessoriesResponse>(`${BASE}/available-accessories`, {
      params,
    });
  },

  /**
   * บันทึกอุปกรณ์เสริม — **ตัดยอดคลังทันที** (ไม่ใช่การจอง) · diff-based ตาม `id` เหมือน
   * `updateItems` จึงต้องส่งครบทุกแถวที่ยังต้องการเก็บไว้เสมอ (แถวที่ขาด = ถูกลบ + คืนยอด)
   * ต้องมี `preparation_jobs.edit`
   */
  async updateAccessories(
    id: number,
    data: PreparationJobAccessoriesUpdateRequest
  ): Promise<PreparationJobActionResponse> {
    return ApiWithLogging.put<PreparationJobActionResponse>(
      `${BASE}/${id}/accessories`,
      data,
      {},
      {
        service: 'preparationJobService',
        action: 'update_preparation_job_accessories',
        entityType: 'preparation_job',
        entityId: id,
      }
    );
  },

  /**
   * ของที่ถูกกันไว้อยู่ตอนนี้ + ค้างมากี่วัน
   * (ใบร่างคงการจองได้ไม่จำกัดเวลา — ต้องมีที่ให้เห็นว่าเครื่องหายไปอยู่ใบไหน)
   *
   * **backend เรียงค้างนานสุดขึ้นก่อนให้แล้ว** (days_reserved มาก→น้อย, tie-break ด้วย item_id)
   * — FE ห้ามเรียงซ้ำ เพราะเมื่อ paginate การเรียงเฉพาะหน้าที่เห็นจะให้ลำดับที่ผิดความจริง
   */
  async getReservedItems(
    options: { staleDays?: number; page?: number; page_size?: number } = {}
  ): Promise<ReservedItemsResponse> {
    const params: Record<string, number> = {
      page: options.page || 1,
      page_size: options.page_size || 50,
    };
    if (typeof options.staleDays === 'number' && options.staleDays > 0) {
      params.stale_days = options.staleDays;
    }
    return ApiWithLogging.get<ReservedItemsResponse>(`${BASE}/reserved-items`, { params });
  },

  /** Export — ส่ง filter ชุดเดียวกับหน้าจอทุกตัว */
  async exportToExcel(
    filters: Omit<PreparationJobFilters, 'page' | 'page_size'> = {}
  ): Promise<Blob> {
    return ApiWithLogging.get<Blob>(
      `${BASE}/export/excel`,
      { params: buildFilterParams(filters), responseType: 'blob' },
      {
        service: 'preparationJobService',
        action: 'export_preparation_jobs_excel',
        entityType: 'preparation_job',
      }
    );
  },
};

export default preparationJobService;
